<template>
    <div
        :class="[
            'tile',
            {
                'tile--open': isOpening,
                'tile--close': isClosing,
                'tile--active': isActive,
                'tile--safari': isSafari
            }
        ]"
        @click="selectTile"
    >
        <div class="tile__token-info"
            :style="{ 'background': `linear-gradient(180deg, #fff 50%, ${partner.mainColour} 1500%)` }"
        >
            <div class="tile__close-icon"
                @click.stop="unselectTile"
            >
                <i class="icon-cancel" />
            </div>

            <div class="tile__token-amount">
                <div class="tile__token-amount--text">
                    {{ $t('wallet.totalAmount') }}
                    <span class="tile__token-amount--total">{{ `${totalTokensAmount} ${ledger.currency.shortCode}` }}</span>
                </div>
                <div class="tile__token-amount--text">
                    {{ $t('wallet.tradable') }}
                    <span>{{ `${Math.round(ledger.balance)} ${ledger.currency.shortCode}` }}</span>
                </div>
                <div class="tile__token-amount--text">
                    {{ $t('wallet.nonTradable') }}
                    <span>{{ `${Math.round(ledger.lockedBalance)} ${ledger.currency.shortCode}` }}</span>
                </div>
            </div>

            <div class="tile__token-logo-price">
                <div class="tile__partner-logo">
                    <partner-logo class="logo"
                        :show-placeholder="true"
                        :url="partner.partnerLogo"
                    />
                    <div class="tile__token-amount--text">
                        {{ $t('wallet.value') }}
                        <div v-if="isChzAllowed">
                            <div class="tile__value--main">{{ getChzAmount }}</div>
                            <div class="tile__value--sub">{{ getTotalFiatAmount }}</div>
                        </div>
                        <div v-else>
                            <div class="tile__value--main">{{ getTotalFiatAmount }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="tile__buttons">
            <div :class="['tile__button', { 'tile__button--disabled': isBuyButtonDisabled }]"
                @click.stop="goToBuy"
            >
                <i v-tooltip="getTooltip($t('marketplace.trading.buyChzToToken', {
                       tokenName: ledger.currency.shortCode
                   }))"
                    class="icon-add"
                />
            </div>
            <div :class="['tile__button', { 'tile__button--disabled': isWithdrawButtonDisabled }]"
                @click.stop="goToWithdraw"
            >
                <i v-tooltip="getTooltip($t('marketplace.trading.withdrawToken', {
                       tokenName: ledger.currency.shortCode
                   }))"
                    class="icon-up"
                />
            </div>
            <div class="tile__button"
                @click.stop="goToTransactionHistory"
            >
                <i v-tooltip="getTooltip($t('marketplace.trading.transactionHistoryForToken', {
                       tokenName: ledger.currency.shortCode
                   }))"
                    class="icon-back-arrow"
                />
            </div>
        </div>
    </div>
</template>

<script>
import partnerLogo from '@webapp/components/partner-logo/partner-logo.vue';

import {
    mapGetters,
} from 'vuex';

// Utils
import {
    formatPrice,
} from '@shared/utils/formatter.js';

import {
    isWithinInterval,
} from '@shared/utils/dates';

// Socios variables
import socios from '@webapp/socios.json';

export default {
    name: 'token-wallet-tile',
    components: {
        partnerLogo,
    },
    props: {
        ledger: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isOpening: false,
            isClosing: false,
            isActive: false,
            isSafari: typeof safari !== 'undefined',
        };
    },
    computed: {
        ...mapGetters([
            'preferredFiatCurrency',
            'voteTokenOfferings',
            'voteTokenOfferingsByStatusAndTokenId',
            'isTokenWithdrawalRestrictedForUser',
            'isPartnerApp',
        ]),

        ...mapGetters('partners', ['partnerById']),

        ...mapGetters('wallet', ['isChzAllowed']),

        ...mapGetters('voteTokens', [
            'voteTokens',
            'getVoteTokenByPartnerId',
            'userTokens',
            'isTokenPurchaseRestricted',
        ]),

        ...mapGetters('marketplace', ['marketByPartnerId']),

        totalTokensAmount() {
            return Math.round(Number(this.ledger.balance) + Number(this.ledger.lockedBalance));
        },
        getTotalFiatAmount() {
            const {
                id,
                shortCode,
            } = this.preferredFiatCurrency;

            return this.getPriceAmount(id, shortCode);
        },
        getChzAmount() {
            return this.getPriceAmount(socios.chilizCurrencyId, '$CHZ');
        },
        partner() {
            const token = this.voteTokens.find(({ currencyId }) => currencyId === this.ledger.currency.id);

            return token ? this.partnerById(token.partnerId) : {};
        },
        voteToken() {
            return this.getVoteTokenByPartnerId(this.partner?.id);
        },
        background() {
            return this.partner.mainColour || '#fff';
        },
        tiles() {
            return document.getElementsByClassName('tile');
        },
        market() {
            return Object.keys(this.partner).length
                ? this.marketByPartnerId(this.partner.id)
                : {};
        },
        isBuyButtonDisabled() {
            if (Object.keys(this.partner).length) {
                return !(this.market.ticker || this.getActiveOffering(this.partner)) ||
                    this.isTokenPurchaseRestricted(this.market.baseCurrency);
            }

            return true;
        },
        isWithdrawButtonDisabled() {
            if (this.isPartnerApp) {
                return true;
            }

            return Object.keys(this.partner).length
                ? (!(this.userTokens().some(({ currency }) => currency.id === this.partner.currencyId)) ||
                    this.isTokenWithdrawalRestrictedForUser(this.voteToken?.shortCode))
                : true;
        },
        buyUrl() {
            if (this.market.ticker) {
                return 'buy-ft';
            }

            if (this.getActiveOffering(this.partner)) {
                return 'buy-fto';
            }

            return '';
        },
    },
    created() {
        window.addEventListener('resize', this.orientationHandler);
    },
    destroyed() {
        window.removeEventListener('resize', this.orientationHandler);
    },
    methods: {
        getPriceAmount(currencyId, currencySymbol) {
            if (this.ledger && this.ledger.convertedPrices && this.ledger.convertedPrices[currencyId]) {
                const total = Number(this.ledger.convertedPrices[currencyId].balance || 0) + Number(this.ledger.convertedPrices[currencyId].lockedBalance || 0);

                return formatPrice(total, currencySymbol, currencySymbol === '$CHZ');
            }

            return '--';
        },
        selectTile(e) {
            if (e.target.closest('.tile.tile--active') || window.innerWidth > 500) {
                return this.partner.id ? this.$router.push({
                    path: `/partner/${this.partner.id}`,
                }) : false;
            }

            this.isOpening = true;
            let active = document.getElementsByClassName('tile--active');

            if (active.length > 0) {
                active = active[0];
                active.classList.add('tile--close');

                setTimeout(() => {
                    active.classList.remove('tile--active');
                    active.classList.remove('tile--close');
                }, 250);
            }

            this.tiles.forEach((element) => {
                if (!element.classList.contains('tile--active') ||
                    !element.classList.contains('tile--inactive') ||
                    !element.classList.contains('tile--open')
                ) {
                    element.classList.add('tile--inactive');
                }
            });

            setTimeout(() => {
                this.isActive = true;
                this.isOpening = false;
            }, 500);
        },
        unselectTile() {
            this.isClosing = true;

            setTimeout(() => {
                this.isActive = false;
                this.isClosing = false;
                this.tiles.forEach((element) => {
                    element.classList.remove('tile--inactive');
                });
            }, 250);
        },
        orientationHandler() {
            if (window.innerWidth > 768) {
                this.tiles.forEach((element) => {
                    element.classList.remove('tile--inactive');
                    element.classList.remove('tile--active');
                });
            }
        },
        goToBuy() {
            if (!this.isBuyButtonDisabled) {
                this.$router.push(`/${this.buyUrl}/${this.partner.id}`);
            }
        },
        goToWithdraw() {
            if (!this.isWithdrawButtonDisabled) {
                this.$router.push({
                    path: '/wallet/withdraw/',
                    query: {
                        tokenId: this.partner.currencyId,
                    },
                });
            }
        },
        goToTransactionHistory() {
            this.$router.push({
                name: 'transactions',
                params: {
                    selectedTokenCode: this.voteToken?.shortCode ?? '',
                },
            });
        },
        getActiveOffering(token) {
            const voteTokenOfferings = this.voteTokenOfferingsByStatusAndTokenId(['opened', 'published'], token.tokenId);

            return socios.showFto &&
                    voteTokenOfferings.length > 0 &&
                    isWithinInterval(new Date(), {
                        start: new Date(voteTokenOfferings[0].startDate),
                        end: new Date(voteTokenOfferings[0].endDate),
                    })
                ? voteTokenOfferings[0] : null;
        },
        getTooltip(tooltip) {
            return {
                content: tooltip,
                delay: 200,
                placement: 'right',
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.tile {
    width: 40.5rem;
    height: 20.5rem;
    display: flex;
    position: relative;
    justify-content: space-around;
    box-shadow: 0 -1rem 1.5rem 0 rgba($black-color, 0.1);
    border-radius: 2rem;
    margin-bottom: 4rem;
    z-index: 1;
    transform: none;
    border: 0.1rem solid $alto-color;

    &:first-of-type {
        margin-top: 2rem;

        @include min-width(md) {
            margin-top: 0;
        }
    }

    @include min-width(lg) {
        margin-right: 4rem;
    }

    @include max-width(sm) {
        width: 100%;
        height: auto;
        margin-top: -17rem;
        transform: rotateX(-20deg);
        background: none;
    }

    &--safari {
        transform: translate3d(0, 0, 0);
    }

    &--inactive {
        filter: blur(0.05rem);
        transition: margin-top linear 0.25s;
        margin-top: -18rem;
    }

    &--close {
        z-index: 1;
        animation: tile-animation-close 0.25s linear;

        @include min-width(md) {
            animation: none;
        }
    }

    &--open {
        z-index: 3;
        animation: tile-animation-open 0.25s linear;

        @include min-width(md) {
            transform: none;
            animation: none;
            transition: none;
        }
    }

    &--open,
    &--close,
    &--active {
        transition: all linear 0.25s;
    }

    &--active {
        z-index: 2;

        .tile__token-info {
            box-shadow: 0 -1rem 1.5rem 0 rgba(0, 0, 0, 0.2);
        }

        @include min-width(md) {
            transform: none;
        }
    }

    &--active,
    &--open {
        top: 0;
        margin-top: -16rem;
        margin-bottom: 18rem;
        transform: rotateX(0) scale(1.1);
        transition: transform linear 0.25s;
        filter: none;

        .tile__close-icon {
            display: flex;
            opacity: 1;

            @include min-width(md) {
                display: none;
            }
        }
    }

    &__token {
        &-info {
            display: flex;
            width: 36.7rem;
            background: linear-gradient(180deg, $white-color 50%, $black-color 1500%);
            border-radius: 2rem;
            padding: 2.4rem 3.8rem;
            box-sizing: border-box;
            z-index: 2;

            &:hover {
                @include min-width(md) {
                    background: $athens-gray-color !important;
                    cursor: pointer;
                }
            }

            > div {
                width: 50%;
            }

            @include max-width(sm) {
                height: 100%;
                width: 100%;
                padding: 1.6rem 1.8rem 1rem;
            }
        }

        &-amount {
            > div {
                margin-bottom: 1rem;
            }

            &--text {
                color: $cyprus-color;
                font-size: 1.1rem;
                line-height: 1.7rem;
                white-space: nowrap;

                @include max-width(sm) {
                    font-size: 0.9rem;
                    line-height: 1.4rem;
                }

                span {
                    display: block;
                    font-size: 1.4rem;
                    line-height: 2.2rem;

                    &.tile__token-amount--total {
                        font-size: 2.5rem;
                        line-height: 3.4rem;
                        font-weight: 700;
                        white-space: nowrap;
                        margin-bottom: 2.1rem;

                        @include max-width(sm) {
                            line-height: 2.1rem;
                            font-size: 2rem;
                            margin-bottom: 3rem;
                        }
                    }
                }
            }
        }

        &-logo-price {
            display: flex;
            justify-content: flex-end;
            text-align: right;
        }
    }

    &__value {
        &--main {
            font-size: 1.6rem;
            line-height: 1.2rem;
            font-weight: 700;
            padding: 0.6rem 0;
        }

        &--sub {
            font-size: 1.2rem;
            line-height: 1.5rem;
            opacity: 0.7;
        }
    }

    &__buttons {
        display: none;
        flex-direction: column;
        justify-content: space-around;
        padding: 4rem 1rem;
        background: $cyprus-color;
        border-top-right-radius: 2rem;
        border-bottom-right-radius: 2rem;

        &:before {
            content: "";
            position: absolute;
            width: 2.5rem;
            height: 100%;
            margin-left: -3.5rem;
            top: 0;
            background: $cyprus-color;
            z-index: 1;
        }

        @include min-width(xl) {
            display: flex;
        }
    }

    &__button {
        width: 1.8rem;
        text-align: center;
        line-height: 1.8rem;
        border-radius: 100%;
        background: $white-color;

        &:hover:not(&--disabled) {
            opacity: 0.9;
            cursor: pointer;
        }

        i {
            position: relative;
            top: 0.1rem;
        }

        &--disabled {
            opacity: 0.3;
        }
    }

    &__partner-logo {
        display: flex;
        flex-direction: column;
    }

    &__close-icon {
        display: none;
        opacity: 0;
        position: absolute;
        font-size: 1rem;
        top: 0.5rem;
        right: 1rem;
        height: 2rem;
        justify-content: flex-end;
        align-items: center;
        color: $alto-color;
        z-index: 3;
        transition: opacity 0.25s ease;
    }

    @keyframes tile-animation-open {
        0% {
            top: 0;
        }

        100% {
            top: -5rem;
        }
    }

    @keyframes tile-animation-close {
        0% {
            top: -5rem;
        }

        100% {
            top: 0;
        }
    }
}

.logo {
    width: 8.6rem;
    height: 8.6rem;
    margin-bottom: 1.7rem;

    @include max-width(sm) {
        width: 7.2rem;
        height: 7.2rem;
        margin-bottom: 2rem;
    }
}
</style>